import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  CONCENTRATION_BASES,
  STRENGTH_BASIS_UNITS,
  STRENGTH_UNITS,
  formStyles,
} from "../../../components/Inventory-Stocks & Orders/Admin/Products/catalogConstants";
import {
  MasterOption,
  ProductCatalogService,
} from "../../../services/api/productCatalog.service";
import { SearchableDropdown } from "../../SearchableDropdown";

export interface SaltRow {
  saltId: string;
  saltName?: string | null;
  strengthValue: string;
  strengthUnit: string;
  strengthBasisValue: string;
  strengthBasisUnit: string;
  concentrationBasis: string;
}

export const emptySaltRow: SaltRow = {
  saltId: "",
  saltName: "",
  strengthValue: "",
  strengthUnit: "mg",
  strengthBasisValue: "1",
  strengthBasisUnit: "Tablet",
  concentrationBasis: "",
};

/**
 * One row's salt box.
 *
 * salt_mstr holds thousands of rows, so the salt is typed and matched rather
 * than picked from a list. State is per row, which is why this is its own
 * component instead of a block inside the map.
 */
function SaltPicker({
  value,
  fallbackName,
  options,
  onSelect,
  isLoading,
}: {
  value: string;
  fallbackName?: string | null;
  options: MasterOption[];
  onSelect: (option: { id: string; name: string }) => void;
  isLoading?: boolean;
}) {
  const [searchText, setSearchText] = useState("");
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedName =
    options.find((option) => option.id === value)?.name || fallbackName || "";

  // Shows the stored salt's name once the options arrive, without touching
  // whatever is currently being typed.
  useEffect(() => {
    if (selectedName && !searchText) setSearchText(selectedName);
  }, [selectedName, searchText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSuggestionsVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <SearchableDropdown
        placeholder={isLoading ? "Loading salts..." : "Type a salt name"}
        searchText={searchText}
        setSearchText={(text) => {
          setSearchText(text);
          // Emptying the box unsets the salt, so a row can never keep an id
          // whose name is no longer shown.
          if (!text.trim()) onSelect({ id: "", name: "" });
        }}
        suggestionsVisible={suggestionsVisible}
        setSuggestionsVisible={setSuggestionsVisible}
        options={options.map((option) => ({
          id: option.id,
          label: option.name,
        }))}
        onSelect={(selected) => {
          onSelect({ id: selected.id, name: selected.label });
          setSearchText(selected.label);
        }}
        dropdownRef={dropdownRef}
        isLoading={isLoading}
      />
      {value && (
        <p className="text-xs text-emerald-600 mt-1">
          Selected: {selectedName || value}
        </p>
      )}
    </>
  );
}

interface ProductSaltDtlsTabProps {
  salts: SaltRow[];
  saltCompositionText: string;
  onChange: (field: string, value: any) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

/** "500 mg per 1 Tablet", or "125 mg / 5 ml" when the basis is more than one. */
const describeStrength = (row: SaltRow) => {
  if (!row.strengthValue) return "";
  const basis =
    Number(row.strengthBasisValue || 1) === 1
      ? row.strengthBasisUnit
      : `${row.strengthBasisValue} ${row.strengthBasisUnit}`;
  return `${row.strengthValue} ${row.strengthUnit} / ${basis}`;
};

/**
 * The product's composition: one row per active ingredient.
 *
 * Salt ids come from salt_mstr, so a salt has to exist in Salt Mapping before a
 * product can use it. Strength is stored as a value, a unit, and what it is
 * measured *per* - the basis - which is how "125mg / 5ml" suspensions and
 * "500mg per tablet" solids share one shape.
 *
 * Saving this replaces the whole composition rather than merging, which is also
 * what recomputes the product's substitute matching.
 */
export const ProductSaltDtlsTab: React.FC<ProductSaltDtlsTabProps> = ({
  salts,
  saltCompositionText,
  onChange,
  isEdit,
  onUpdate,
  isLoading,
}) => {
  const [saltOptions, setSaltOptions] = useState<MasterOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await ProductCatalogService.getSaltOptions({ isActive: true });
      if (cancelled) return;
      setSaltOptions(res?.success ? res.data || [] : []);
      setIsLoadingOptions(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateRow = (index: number, patch: Partial<SaltRow>) => {
    onChange(
      "salts",
      salts.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      )
    );
  };

  const addRow = () => onChange("salts", [...salts, { ...emptySaltRow }]);

  const removeRow = (index: number) =>
    onChange(
      "salts",
      salts.filter((_row, rowIndex) => rowIndex !== index)
    );

  /** Preview of what the backend will treat as a complex composition. */
  const compositionSummary = useMemo(
    () =>
      salts
        .filter((row) => row.saltId)
        .map((row) => {
          const name =
            saltOptions.find((option) => option.id === row.saltId)?.name ||
            row.saltName ||
            row.saltId;
          const strength = describeStrength(row);
          return strength ? `${name} ${strength}` : name;
        })
        .join(" + "),
    [salts, saltOptions]
  );

  const usedSaltIds = salts.map((row) => row.saltId).filter(Boolean);

  return (
    <div className="space-y-6">
      <section className={formStyles.sectionCard}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className={formStyles.sectionTitle}>Composition</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              One row per active ingredient. Salts must exist in Salt Mapping
              first.
            </p>
          </div>
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Salt
          </button>
        </div>

        {salts.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
            No salts added yet. A medicine without a composition cannot be
            matched to substitutes.
          </p>
        ) : (
          <div className="space-y-4">
            {salts.map((row, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50/50"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Salt {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove salt"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <label className={formStyles.lableStyle}>
                      Salt<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <SaltPicker
                      value={row.saltId}
                      fallbackName={row.saltName}
                      isLoading={isLoadingOptions}
                      // A salt already used by another row is not offered again.
                      options={saltOptions.filter(
                        (option) =>
                          option.id === row.saltId ||
                          !usedSaltIds.includes(option.id)
                      )}
                      onSelect={(selected) =>
                        updateRow(index, {
                          saltId: selected.id,
                          saltName: selected.name,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className={formStyles.lableStyle}>Strength</label>
                    <input
                      type="number"
                      step="any"
                      value={row.strengthValue}
                      onChange={(e) =>
                        updateRow(index, { strengthValue: e.target.value })
                      }
                      className={formStyles.inputStyle}
                      placeholder="e.g. 500"
                    />
                  </div>

                  <div>
                    <label className={formStyles.lableStyle}>
                      Strength Unit
                    </label>
                    <select
                      value={row.strengthUnit}
                      onChange={(e) =>
                        updateRow(index, { strengthUnit: e.target.value })
                      }
                      className={formStyles.inputStyle}
                    >
                      <option value="">Select unit</option>
                      {STRENGTH_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={formStyles.lableStyle}>
                      Concentration Basis
                    </label>
                    <select
                      value={row.concentrationBasis}
                      onChange={(e) =>
                        updateRow(index, { concentrationBasis: e.target.value })
                      }
                      className={formStyles.inputStyle}
                    >
                      <option value="">Not applicable</option>
                      {CONCENTRATION_BASES.map((basis) => (
                        <option key={basis} value={basis}>
                          {basis}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={formStyles.lableStyle}>Per (value)</label>
                    <input
                      type="number"
                      step="any"
                      value={row.strengthBasisValue}
                      onChange={(e) =>
                        updateRow(index, { strengthBasisValue: e.target.value })
                      }
                      className={formStyles.inputStyle}
                      placeholder="1"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      The 5 in "125mg / 5ml". Defaults to 1.
                    </p>
                  </div>

                  <div>
                    <label className={formStyles.lableStyle}>Per (unit)</label>
                    <select
                      value={row.strengthBasisUnit}
                      onChange={(e) =>
                        updateRow(index, { strengthBasisUnit: e.target.value })
                      }
                      className={formStyles.inputStyle}
                    >
                      <option value="">Select unit</option>
                      {STRENGTH_BASIS_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <p className="text-sm text-emerald-700 font-medium">
                      {describeStrength(row) || "—"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className={formStyles.sectionCard}>
        <h4 className={`${formStyles.sectionTitle} mb-3`}>
          Composition As Printed
        </h4>
        <label className={formStyles.lableStyle}>
          Salt Composition Text
        </label>
        <textarea
          value={saltCompositionText}
          onChange={(e) => onChange("saltCompositionText", e.target.value)}
          className={`${formStyles.inputStyle} h-20 resize-none`}
          placeholder="Copy the composition exactly as printed on the pack"
        />
        {compositionSummary && (
          <div className="mt-3 flex items-start gap-2">
            <span className="text-xs text-gray-500 shrink-0 mt-0.5">
              From the rows above:
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-700">
                {compositionSummary}
              </span>
              <button
                type="button"
                onClick={() =>
                  onChange("saltCompositionText", compositionSummary)
                }
                className="text-xs font-medium text-emerald-600 hover:text-emerald-800"
              >
                Use this
              </button>
            </div>
          </div>
        )}
      </section>

      {isEdit && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update Composition"}
          </button>
        </div>
      )}
    </div>
  );
};
